import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { fmtEur } from '../../lib/utils';
import {
  TAX_KINDS, TAX_KIND_LABEL, isSuperAdmin,
  type TaxKind, type TaxSource, type Entity, type TaxObligation,
} from '../../features/admin/adminApi';

interface Prefill {
  kind?: TaxKind;
  year?: number;
  period?: string;
  description?: string;
  amountDue?: number;
  dueDate?: string;
  source?: TaxSource;
}

/**
 * Scadenza fiscale (F24, IVA, INPS, rate): tipo, anno/periodo, descrizione,
 * importo, scadenza, fonte (Stima / Confermato), nota. Con `tax` modifica
 * l'esistente (PATCH). L'entità la sceglie solo il Super Admin.
 * Le rate si aggiungono dall'API (/taxes/:id/installments): qui una scadenza sola.
 */
export function NewTaxModal({ onClose, tax, prefill }: { onClose: () => void; tax?: TaxObligation; prefill?: Prefill }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const editing = !!tax;
  const [form, setForm] = useState({
    kind: (tax?.kind ?? prefill?.kind ?? 'F24_DIPENDENTI') as TaxKind,
    year: String(tax?.year ?? prefill?.year ?? new Date().getFullYear()),
    period: tax?.period ?? prefill?.period ?? '',
    description: tax?.description ?? prefill?.description ?? '',
    amountDue: tax ? String(tax.amountDue) : prefill?.amountDue != null ? String(prefill.amountDue) : '',
    dueDate: (tax?.dueDate ?? prefill?.dueDate ?? '').slice(0, 10),
    source: (tax?.source ?? prefill?.source ?? 'CONFERMATO') as TaxSource,
    note: tax?.note ?? '',
    entity: (tax?.entity ?? 'MASAMUNE') as Entity,
  });
  const amount = Number(String(form.amountDue).replace(',', '.'));

  const payload = () => ({
    kind: form.kind,
    year: Number(form.year),
    period: form.period.trim() || null,
    description: form.description.trim() || `${TAX_KIND_LABEL[form.kind]} ${form.year}`,
    amountDue: amount,
    dueDate: form.dueDate || null,
    source: form.source,
    note: form.note.trim() || null,
    entity: form.entity,
  });

  const save = useMutation({
    mutationFn: () => editing
      ? api.patch(`/admin/taxes/${tax!.id}`, payload())
      : api.post<{ id: string }>('/admin/taxes', { ...payload(), status: 'APERTA' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast.ok(editing ? 'Scadenza aggiornata' : 'Scadenza creata', `${form.description || TAX_KIND_LABEL[form.kind]} · € ${fmtEur(amount, true)}`);
      onClose();
    },
    onError: (err) => toast.alert('Non salvata', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = Number.isFinite(amount) && amount >= 0 && !!form.year && !save.isPending;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); save.mutate(); }
  };

  return (
    <Modal
      title={editing ? 'Modifica scadenza' : 'Scadenza fiscale'}
      kicker="Amministrazione · Fisco"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={!canSubmit}>
            {editing ? 'Salva' : 'Crea'} <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field label="Tipo" span={7}>
          <select className="inp" autoFocus value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as TaxKind })}>
            {TAX_KINDS.map((k) => <option key={k} value={k}>{TAX_KIND_LABEL[k]}</option>)}
          </select>
        </Field>
        <Field label="Anno" required span={5}>
          <input className="inp mono" inputMode="numeric" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
        </Field>
        <Field label="Descrizione" span={8} hint="Facoltativa: se vuota usa tipo e anno">
          <input className="inp" placeholder="Es. IVA Q3 2026, F24 dipendenti settembre" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label="Periodo" span={4} hint="Es. Q3, 09">
          <input className="inp mono" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
        </Field>
        <Field label="Importo" required span={6}>
          <input className="inp mono" inputMode="decimal" placeholder="0,00" value={form.amountDue} onChange={(e) => setForm({ ...form, amountDue: e.target.value })} />
        </Field>
        <Field label="Scadenza" span={6}>
          <input className="inp mono" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
        </Field>
        <Field label="Fonte" span={isSuperAdmin(me) ? 6 : 12} hint="Stima = dal report, da confermare col commercialista">
          <div className="seg-control">
            <button type="button" aria-pressed={form.source === 'STIMA'} onClick={() => setForm({ ...form, source: 'STIMA' })}>Stima</button>
            <button type="button" aria-pressed={form.source === 'CONFERMATO'} onClick={() => setForm({ ...form, source: 'CONFERMATO' })}>Confermato</button>
          </div>
        </Field>
        {isSuperAdmin(me) && (
          <Field label="Di chi" span={6}>
            <div className="seg-control">
              <button type="button" aria-pressed={form.entity === 'MASAMUNE'} onClick={() => setForm({ ...form, entity: 'MASAMUNE' })}>Masamune</button>
              <button type="button" aria-pressed={form.entity === 'PERSONALE_ANDREA'} onClick={() => setForm({ ...form, entity: 'PERSONALE_ANDREA' })}>Personale</button>
            </div>
          </Field>
        )}
        <Field label="Nota" span={12}>
          <textarea className="inp" rows={2} placeholder="Facoltativa" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
